import { ECommerceOnboarding, ECommerceOnboardingStep } from "consts/ecommerce";
import { withDetectIncomplete } from "features/ecommerce/helpers";
import type { Merchant } from "features/ecommerce/types";
import useApplication from "features/ecommerce/useOnboardingApplication";
import { useOnboardingApplication } from "ui/organizms/ECommerce/Onboarding/common";

const useOnboardingMerchantApplication = () => {
    const { response } = useApplication<Merchant.OnboardingApplication>();

    const getApplicationModel = () => {
        const {
            isIncompleteBillingFinanceStep,
            isIncompleteBusinessInformationStep,
            isIncompleteIdentityVerificationStep,
            isIncompleteMediaComplianceStep,
            isIncompletePaymentMethodsStep,

            hasRequestedChangeBusinessInformationStep = false,
            hasRequestedChangeBillingFinanceStep = false,
            hasRequestedChangeMediaComplianceStep = false,
            hasRequestedChangeIdentityVerificationStep = false,
            hasRequestedChangePaymentMethodsStep = false,

            hasAgreedTermsConditions = false
        } = response.data ?? {};

        return new Map<number, {
            readonly index: number;
            readonly isIncomplete: boolean;
            readonly hasRequestedChange: boolean;
            readonly countable?: boolean;
            readonly layout?: {
                readonly stepContainerSize: string | number;
            }
        }>()
            .set(ECommerceOnboardingStep[ECommerceOnboarding.Merchant].GettingStarted, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.Merchant].GettingStarted,
                isIncomplete: !response.data,
                hasRequestedChange: false,
                countable: false
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.Merchant].BusinessInformation, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.Merchant].BusinessInformation,
                isIncomplete: withDetectIncomplete(isIncompleteBusinessInformationStep),
                hasRequestedChange: Boolean(hasRequestedChangeBusinessInformationStep)
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.Merchant].BankingAndFinance, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.Merchant].BankingAndFinance,
                isIncomplete: withDetectIncomplete(isIncompleteBillingFinanceStep),
                hasRequestedChange: Boolean(hasRequestedChangeBillingFinanceStep)
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.Merchant].MediaAndComplianceCheck, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.Merchant].MediaAndComplianceCheck,
                isIncomplete: withDetectIncomplete(isIncompleteMediaComplianceStep),
                hasRequestedChange: Boolean(hasRequestedChangeMediaComplianceStep)
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.Merchant].UBOIdentityVerification, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.Merchant].UBOIdentityVerification,
                isIncomplete: withDetectIncomplete(isIncompleteIdentityVerificationStep),
                hasRequestedChange: Boolean(hasRequestedChangeIdentityVerificationStep)
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.Merchant].PaymentMethods, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.Merchant].PaymentMethods,
                isIncomplete: withDetectIncomplete(isIncompletePaymentMethodsStep),
                hasRequestedChange: Boolean(hasRequestedChangePaymentMethodsStep)
            })
            .set(ECommerceOnboardingStep[ECommerceOnboarding.Merchant].CompleteOnboarding, {
                index: ECommerceOnboardingStep[ECommerceOnboarding.Merchant].CompleteOnboarding,
                isIncomplete: !hasAgreedTermsConditions,
                hasRequestedChange: false,
                countable: false
            });
    };

    return {
        ...useOnboardingApplication({
            getApplicationModel
        }),
        getApplicationModel
    };
};

export {
    useOnboardingMerchantApplication as useOnboardingApplication
};
